import React from 'react'
import { DialogTitle, DialogContent, DialogActions, Box, Typography } from '@mui/material'
import { ThemeButton } from '../theme-button'

/**
 * @typedef {object} AcceptationDialogProps
 * @property {string} [title='Warning:'] - El título que se muestra en la parte superior del diálogo.
 * @property {string} [text='Are you sure you want to proceed?'] - El mensaje o cuerpo principal del diálogo.
 * @property {string} [yesText='Yes'] - El texto que se mostrará en el botón de acción.
 * @property {string} [yesColor='primary'] - El color del botón de acción, basado en los colores del tema de Material-UI.
 * @property {function(): void} [yesAction] - La función que se ejecuta cuando el usuario hace clic en el botón de acción.
 * @property {boolean} [yesAndClose=true] - Si es `true`, el diálogo se cerrará automáticamente después de ejecutar `yesAction`.
 * @property {function(): void} close - La función, generalmente proporcionada por un componente `Dialog` padre, para cerrar el modal. Es requerida para el funcionamiento del cierre.
 */

/**
 * Un componente de diálogo simple diseñado para mostrar un mensaje informativo y un único botón de acción.
 *
 * Es ideal para notificaciones, alertas o mensajes que solo requieren que el usuario acuse recibo
 * (ej. "Operación completada con éxito"). El comportamiento del botón y el cierre del diálogo son configurables.
 *
 * **Nota:** Este componente está diseñado para ser renderizado dentro de un contenedor de diálogo, como el componente `Dialog` de Material-UI.
 *
 * @component
 * @example
 * import { Dialog } from '@mui/material';
 *
 * const MyPage = () => {
 * const [open, setOpen] = useState(false);
 *
 * const handleAcknowledge = () => {
 * console.log('Mensaje aceptado por el usuario.');
 * };
 *
 * return (
 * <>
 * <button onClick={() => setOpen(true)}>Mostrar Notificación</button>
 * <Dialog open={open} onClose={() => setOpen(false)}>
 * <AcceptationDialog
 * title="Operación Exitosa"
 * text="El registro se ha guardado correctamente."
 * yesText="Aceptar"
 * yesAction={handleAcknowledge}
 * close={() => setOpen(false)}
 * />
 * </Dialog>
 * </>
 * );
 * };
 *
 * @param {AcceptationDialogProps} props - Las props del componente AcceptationDialog.
 */
export const AcceptationDialog = ({
  title = 'Warning:',
  text = 'Are you sure you want to proceed?',
  yesText = 'Yes',
  yesColor = 'primary',
  yesAction = () => {},
  yesAndClose = true,
  close,
}) => {
  return (
    <Box p={4}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="center">
          <Typography variant="h4" color="primary">
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{text}</Typography>
      </DialogContent>
      <DialogActions>
        <ThemeButton
          labelM="small"
          color={yesColor}
          onClick={() => {
            yesAction()
            if (yesAndClose) {
              close()
            }
          }}
        >
          {yesText}
        </ThemeButton>
      </DialogActions>
    </Box>
  )
}
